/**
 * Chord parsing, matching and spelling (架构方案 §5.9 / 见 03).
 *
 * **A chord identifies a physical key, not a character.** Declarations are
 * written the way a US keyboard reads (`ctrl+shift+p`, `ctrl+,`) and compiled to
 * a `KeyboardEvent.code`, which is what the event is matched on. Matching on
 * `event.key` — what this used to do — cannot work: on a German layout the
 * `Ctrl+=` press arrives as `key: "Dead"`, so that whole class of chord becomes
 * unbindable, and every layout that moves a character silently moves the
 * shortcut with it. VS Code reached the same conclusion (microsoft/vscode#17521)
 * and dispatches on scan codes for the same reason.
 *
 * The cost is that a chord sits at a *position*, so on a non-US layout the label
 * printed on that key is not what the declaration says. {@link formatChord}
 * closes that gap by asking the browser what the current layout produces there
 * (`navigator.keyboard.getLayoutMap()`), which is the layout-aware half VS Code
 * needs a native module for.
 */

import { readHostOs } from '@hibro/shared';

/** A normalized chord: modifier state plus one physical key. */
export interface Chord {
  readonly ctrl: boolean;
  readonly shift: boolean;
  readonly alt: boolean;
  readonly meta: boolean;
  /** `KeyboardEvent.code` of the non-modifier key, e.g. `KeyP` / `Comma`. */
  readonly code: string;
}

/**
 * Declaration spelling → physical key, as a US layout reads.
 *
 * The names are the contract with manifest authors, so they stay conservative:
 * one spelling per key, plus the few aliases people reliably reach for
 * (`esc`, `del`, `up`).
 */
const KEY_TO_CODE: Readonly<Record<string, string>> = {
  ...Object.fromEntries(
    'abcdefghijklmnopqrstuvwxyz'
      .split('')
      .map((letter) => [letter, `Key${letter.toUpperCase()}`]),
  ),
  ...Object.fromEntries(
    Array.from({ length: 10 }, (_unused, digit) => [
      String(digit),
      `Digit${String(digit)}`,
    ]),
  ),
  ...Object.fromEntries(
    Array.from({ length: 24 }, (_unused, index) => [
      `f${String(index + 1)}`,
      `F${String(index + 1)}`,
    ]),
  ),
  '-': 'Minus',
  '=': 'Equal',
  '[': 'BracketLeft',
  ']': 'BracketRight',
  '\\': 'Backslash',
  ';': 'Semicolon',
  "'": 'Quote',
  ',': 'Comma',
  '.': 'Period',
  '/': 'Slash',
  '`': 'Backquote',
  enter: 'Enter',
  return: 'Enter',
  escape: 'Escape',
  esc: 'Escape',
  space: 'Space',
  tab: 'Tab',
  backspace: 'Backspace',
  delete: 'Delete',
  del: 'Delete',
  insert: 'Insert',
  home: 'Home',
  end: 'End',
  pageup: 'PageUp',
  pagedown: 'PageDown',
  up: 'ArrowUp',
  down: 'ArrowDown',
  left: 'ArrowLeft',
  right: 'ArrowRight',
  arrowup: 'ArrowUp',
  arrowdown: 'ArrowDown',
  arrowleft: 'ArrowLeft',
  arrowright: 'ArrowRight',
};

/** Physical key → the declaration spelling, for round-tripping a recorded key. */
const CODE_TO_KEY: Readonly<Record<string, string>> = Object.fromEntries(
  Object.entries(KEY_TO_CODE)
    // Later aliases (`esc`, `del`, `arrowup`) must not win over the canonical
    // spelling, so only the first entry per code is kept.
    .reverse()
    .map(([key, code]) => [code, key]),
);

/**
 * Physical key → Electron accelerator token (see `globalShortcut`). Only the
 * keys whose accelerator name differs from the declaration spelling are listed;
 * everything else falls back to the uppercased spelling.
 */
const CODE_TO_ACCELERATOR: Readonly<Record<string, string>> = {
  Enter: 'Return',
  ArrowUp: 'Up',
  ArrowDown: 'Down',
  ArrowLeft: 'Left',
  ArrowRight: 'Right',
  Minus: '-',
  Equal: '=',
  BracketLeft: '[',
  BracketRight: ']',
  Backslash: '\\',
  Semicolon: ';',
  Quote: "'",
  Comma: ',',
  Period: '.',
  Slash: '/',
  Backquote: '`',
};

/** Symbols a mac renders a modifier with; every other platform spells them out. */
const MAC_MODIFIER_SYMBOL = {
  ctrl: '⌃',
  alt: '⌥',
  shift: '⇧',
  meta: '⌘',
} as const;

/** Display names for keys whose spelling is not a printable character. */
const CODE_TO_LABEL: Readonly<Record<string, string>> = {
  Enter: 'Enter',
  Escape: 'Esc',
  Space: 'Space',
  Tab: 'Tab',
  Backspace: 'Backspace',
  Delete: 'Delete',
  Insert: 'Insert',
  Home: 'Home',
  End: 'End',
  PageUp: 'PageUp',
  PageDown: 'PageDown',
  ArrowUp: '↑',
  ArrowDown: '↓',
  ArrowLeft: '←',
  ArrowRight: '→',
};

/**
 * Whether this renderer is on macOS, which decides what `mod` resolves to and
 * how a chord is spelled.
 *
 * Reads the host stamp both runtimes put on `window` before the shell boots
 * (见 18), and only falls back to sniffing the UA string when it is absent —
 * a detached window loaded outside the normal entry, or a unit test.
 */
export function isMacPlatform(): boolean {
  const os = readHostOs();
  if (os !== undefined) {
    return os === 'darwin';
  }
  return /mac/i.test(navigator.userAgent);
}

export interface ParseChordOptions {
  /** Overrides {@link isMacPlatform}; the resolution of `mod` depends on it. */
  mac?: boolean;
}

/**
 * Parse a declarative combo (`mod+shift+p`) into a chord, or `undefined` when it
 * names no key this platform can match.
 *
 * `undefined` rather than a chord with an empty key: an unparseable declaration
 * is a typo in a manifest, and the only way its author finds out is if the
 * registry can tell it apart from a chord that simply has not been pressed yet.
 */
export function parseChord(
  combo: string,
  options: ParseChordOptions = {},
): Chord | undefined {
  const mac = options.mac ?? isMacPlatform();
  let ctrl = false;
  let shift = false;
  let alt = false;
  let meta = false;
  let code: string | undefined;

  for (const raw of combo.toLowerCase().split('+')) {
    const part = raw.trim();
    if (part === '') {
      // A trailing `+` is how `ctrl++` spells the plus key; anything else that
      // yields an empty part is noise the split already dropped.
      continue;
    }
    switch (part) {
      case 'mod':
        // One declaration, both platforms (见 03): the Cmd/Ctrl split is the
        // only per-platform difference worth a modifier of its own, and the
        // alternative — two `when`-gated copies of every chord — doubles the
        // table for no expressive gain.
        if (mac) {
          meta = true;
        } else {
          ctrl = true;
        }
        break;
      case 'ctrl':
      case 'control':
        ctrl = true;
        break;
      case 'cmd':
      case 'meta':
      case 'super':
      case 'win':
        meta = true;
        break;
      case 'shift':
        shift = true;
        break;
      case 'alt':
      case 'option':
        alt = true;
        break;
      default:
        code = KEY_TO_CODE[part];
        if (code === undefined) {
          return undefined;
        }
    }
  }

  return code === undefined ? undefined : { ctrl, shift, alt, meta, code };
}

/** Whether a parsed binding chord matches a chord derived from an event. */
export function chordMatches(binding: Chord, event: Chord): boolean {
  return (
    binding.ctrl === event.ctrl &&
    binding.shift === event.shift &&
    binding.alt === event.alt &&
    binding.meta === event.meta &&
    binding.code === event.code
  );
}

/** The parts of a `KeyboardEvent` a chord is derived from. */
export type ChordKeyEvent = Pick<
  KeyboardEvent,
  'code' | 'ctrlKey' | 'shiftKey' | 'altKey' | 'metaKey'
>;

export function eventToChord(event: ChordKeyEvent): Chord {
  return {
    ctrl: event.ctrlKey,
    shift: event.shiftKey,
    alt: event.altKey,
    meta: event.metaKey,
    code: event.code,
  };
}

/** Whether a `code` belongs to a modifier key rather than a chord's key. */
export function isModifierCode(code: string): boolean {
  return (
    code.startsWith('Control') ||
    code.startsWith('Shift') ||
    code.startsWith('Alt') ||
    code.startsWith('Meta') ||
    code === 'CapsLock'
  );
}

/** Render a chord back into the declaration form (`ctrl+shift+p`). */
export function stringifyChord(chord: Chord): string {
  const parts: string[] = [];
  if (chord.ctrl) {
    parts.push('ctrl');
  }
  if (chord.shift) {
    parts.push('shift');
  }
  if (chord.alt) {
    parts.push('alt');
  }
  if (chord.meta) {
    parts.push('cmd');
  }
  parts.push(CODE_TO_KEY[chord.code] ?? chord.code.toLowerCase());
  return parts.join('+');
}

/**
 * What the current keyboard layout produces for each physical key, keyed by
 * `code`. Absent when the browser has no Keyboard API.
 */
export type KeyboardLayoutLabels = ReadonlyMap<string, string>;

interface KeyboardLayoutCapableNavigator {
  keyboard?: { getLayoutMap?: () => Promise<KeyboardLayoutLabels> };
}

/**
 * Read the current layout so chords can be *shown* with the character actually
 * printed on the key (`ctrl+/` displays as `Ctrl+#` on a Swiss German layout).
 *
 * Resolves `undefined` where the API is missing (Safari, Firefox) instead of
 * throwing: this only affects the label, and a chord labelled by its US
 * spelling still fires correctly, so there is nothing to fail closed on.
 */
export async function readKeyboardLayout(): Promise<
  KeyboardLayoutLabels | undefined
> {
  const keyboard = (navigator as unknown as KeyboardLayoutCapableNavigator)
    .keyboard;
  if (!keyboard?.getLayoutMap) {
    return undefined;
  }
  return keyboard.getLayoutMap();
}

export interface FormatChordOptions {
  /** Overrides {@link isMacPlatform}; decides symbols vs. spelled-out names. */
  mac?: boolean;
  /** From {@link readKeyboardLayout}; without it the US spelling is shown. */
  layout?: KeyboardLayoutLabels;
}

/** Human-facing spelling of a chord: `⌘⇧P` on macOS, `Ctrl+Shift+P` elsewhere. */
export function formatChord(
  chord: Chord,
  options: FormatChordOptions = {},
): string {
  const mac = options.mac ?? isMacPlatform();
  const key = formatChordKey(chord.code, options.layout);
  if (mac) {
    // macOS convention: symbols, no separator, and a fixed modifier order.
    return (
      (chord.ctrl ? MAC_MODIFIER_SYMBOL.ctrl : '') +
      (chord.alt ? MAC_MODIFIER_SYMBOL.alt : '') +
      (chord.shift ? MAC_MODIFIER_SYMBOL.shift : '') +
      (chord.meta ? MAC_MODIFIER_SYMBOL.meta : '') +
      key
    );
  }
  const parts: string[] = [];
  if (chord.ctrl) {
    parts.push('Ctrl');
  }
  if (chord.meta) {
    parts.push('Win');
  }
  if (chord.alt) {
    parts.push('Alt');
  }
  if (chord.shift) {
    parts.push('Shift');
  }
  parts.push(key);
  return parts.join('+');
}

function formatChordKey(code: string, layout?: KeyboardLayoutLabels): string {
  const named = CODE_TO_LABEL[code];
  if (named !== undefined) {
    // Named keys (Enter, arrows) are not "characters the layout produces", and
    // `getLayoutMap` does not carry them — only the writing-system keys.
    return named;
  }
  const produced = layout?.get(code);
  if (produced !== undefined && produced.length > 0) {
    return produced.toUpperCase();
  }
  return (CODE_TO_KEY[code] ?? code).toUpperCase();
}

/**
 * Electron accelerator spelling for an OS-level grab, or `undefined` when the
 * chord has none.
 *
 * Emits concrete `Control` / `Command` rather than `CommandOrControl`: `mod` was
 * already resolved against this platform at parse time, and the main process
 * runs on that same platform, so re-introducing the ambiguity here could only
 * make the two disagree.
 */
export function toAccelerator(chord: Chord): string | undefined {
  const key =
    CODE_TO_ACCELERATOR[chord.code] ??
    (CODE_TO_KEY[chord.code]?.toUpperCase() || undefined);
  if (key === undefined) {
    return undefined;
  }
  const parts: string[] = [];
  if (chord.ctrl) {
    parts.push('Control');
  }
  if (chord.meta) {
    parts.push('Command');
  }
  if (chord.alt) {
    parts.push('Alt');
  }
  if (chord.shift) {
    parts.push('Shift');
  }
  if (parts.length === 0) {
    // A bare key as a global grab would swallow that key for the whole desktop.
    return undefined;
  }
  parts.push(key);
  return parts.join('+');
}

/**
 * Whether the browser keeps this chord for itself, making it unbindable on the
 * web runtime (见 18).
 *
 * The list is the set a page provably cannot `preventDefault`: tab and window
 * lifecycle, plus devtools. There *is* an API that would capture them — Keyboard
 * Lock — but it only applies inside a JavaScript-initiated fullscreen and is not
 * Baseline, so a workbench cannot use it without holding the screen hostage.
 * Reporting the chord as unavailable is therefore the honest answer, not a
 * limitation waiting for a workaround.
 */
export function isBrowserReservedChord(
  chord: Chord,
  options: ParseChordOptions = {},
): boolean {
  const mac = options.mac ?? isMacPlatform();
  const primary = mac ? chord.meta : chord.ctrl;
  if (chord.code === 'F11' && !primary && !chord.alt && !chord.shift) {
    return true;
  }
  if (!primary) {
    return false;
  }
  // Tab/window lifecycle: the browser acts on these before the page sees them.
  if (['KeyW', 'KeyT', 'KeyN', 'KeyQ'].includes(chord.code)) {
    return true;
  }
  // Devtools, which Chromium reserves whenever the primary modifier + Shift is
  // held (and on macOS with Option instead of Shift).
  return (
    (chord.shift || (mac && chord.alt)) &&
    ['KeyI', 'KeyJ', 'KeyC'].includes(chord.code)
  );
}
